/** @param {Date | string} d */
export function toDateKey(d) {
  const x = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(x.getTime())) return null;
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, '0');
  const day = String(x.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** @param {string} key YYYY-MM-DD */
export function parseDateKey(key) {
  if (!key || typeof key !== 'string') return null;
  const [y, m, d] = key.split('-').map(Number);
  if (!y || !m || !d) return null;
  const dt = new Date(y, m - 1, d);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

/** @param {string} key */
export function addDaysToKey(key, delta) {
  const dt = parseDateKey(key);
  if (!dt) return key;
  dt.setDate(dt.getDate() + delta);
  return toDateKey(dt);
}

/**
 * Move a YYYY-MM-DD to the same calendar day in a target year/month (day clamped to the last day of that month).
 * @param {string} dateKey
 * @param {number} targetYear
 * @param {number} targetMonth0 0–11
 * @returns {string | null}
 */
export function moveDateToMonth(dateKey, targetYear, targetMonth0) {
  const dt = parseDateKey(dateKey);
  if (!dt) return null;
  const day = dt.getDate();
  const lastDay = new Date(targetYear, targetMonth0 + 1, 0).getDate();
  const newDay = Math.min(day, lastDay);
  return toDateKey(new Date(targetYear, targetMonth0, newDay));
}

/**
 * @param {number} year
 * @param {number} month 0-11
 * @param {{ weekStartsOn?: 0 | 1 }} [opts] 0 = Sunday (default), 1 = Monday
 * @returns {{ dateKey: string | null, inMonth: boolean, jsDate: Date }[]}
 */
export function buildMonthGrid(year, month, opts = {}) {
  const weekStartsOn = opts.weekStartsOn === 1 ? 1 : 0;
  const first = new Date(year, month, 1);
  const startDow = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  const pad = weekStartsOn === 1 ? (startDow + 6) % 7 : startDow;
  for (let i = 0; i < pad; i += 1) {
    const d = new Date(year, month, 1 - (pad - i));
    cells.push({ dateKey: toDateKey(d), inMonth: false, jsDate: d });
  }
  for (let day = 1; day <= daysInMonth; day += 1) {
    const d = new Date(year, month, day);
    cells.push({ dateKey: toDateKey(d), inMonth: true, jsDate: d });
  }
  while (cells.length % 7 !== 0) {
    const last = cells[cells.length - 1].jsDate;
    const d = new Date(last);
    d.setDate(d.getDate() + 1);
    cells.push({ dateKey: toDateKey(d), inMonth: false, jsDate: d });
  }
  return cells;
}

/** Split flat month grid into rows of 7 (Sun–Sat). */
export function chunkCalendarWeeks(cells) {
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) {
    weeks.push(cells.slice(i, i + 7));
  }
  return weeks;
}

export const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const WEEKDAY_LABELS_MONDAY = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * Master Tour Calendar month screenshots use Sunday columns (Sep 1 2026 = Tuesday).
 * The HTML prototype defaulted to Monday; the client shots win.
 */
export const MASTER_TOUR_WEEK_STARTS_ON = 0;

/** @param {0 | 1} weekStartsOn */
export function weekdayLabelsForWeekStart(weekStartsOn) {
  return weekStartsOn === 1 ? WEEKDAY_LABELS_MONDAY : WEEKDAY_LABELS;
}

/** Sunday-start or Monday-start of the week containing `date`. */
export function startOfWeek(date, weekStartsOn = 0) {
  const d = new Date(date);
  const dow = d.getDay();
  const back = weekStartsOn === 1 ? (dow + 6) % 7 : dow;
  d.setDate(d.getDate() - back);
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Seven days of the week containing `date`, for the persistent weekday bar. */
export function weekDaysFromAnchor(date, weekStartsOn = 0) {
  const start = startOfWeek(date instanceof Date ? date : new Date(date), weekStartsOn);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return { date: d, dateKey: toDateKey(d), dow: WEEKDAY_LABELS[d.getDay()] };
  });
}

/**
 * Inclusive YYYY-MM-DD run from startKey through endKey (order-insensitive).
 * Used so the day strip can cross month boundaries instead of clipping to the pager month.
 * @param {string} startKey
 * @param {string} endKey
 * @returns {string[]}
 */
export function contiguousDateKeys(startKey, endKey) {
  const start = parseDateKey(startKey);
  const end = parseDateKey(endKey);
  if (!start || !end) return [];
  const a = start.getTime() <= end.getTime() ? start : end;
  const b = start.getTime() <= end.getTime() ? end : start;
  const keys = [];
  const d = new Date(a.getFullYear(), a.getMonth(), a.getDate());
  const last = new Date(b.getFullYear(), b.getMonth(), b.getDate());
  while (d.getTime() <= last.getTime()) {
    keys.push(toDateKey(d));
    d.setDate(d.getDate() + 1);
  }
  return keys;
}

/**
 * Inclusive YYYY-MM-DD run covering the selected tour's dated cards.
 * Falls back to the pager month when the tour has no dated days yet.
 */
export function tourTimelineDateKeys({ tourDayMetaByDate, selectedTourFilterId, year, month } = {}) {
  const hits = Object.keys(tourDayMetaByDate || {})
    .filter((key) => /^\d{4}-\d{2}-\d{2}$/.test(key))
    .filter((key) => {
      const meta = tourDayMetaByDate[key];
      const tours = Array.isArray(meta?.tours) ? meta.tours : [];
      if (selectedTourFilterId) {
        return tours.some((t) => String(t.tourId) === String(selectedTourFilterId) && t?.card?.kind);
      }
      return Boolean((tours.find((t) => t?.card?.kind) || meta?.card)?.kind);
    })
    .sort();
  if (hits.length) return contiguousDateKeys(hits[0], hits[hits.length - 1]);
  const y = Number(year);
  const m = Number(month);
  if (!Number.isFinite(y) || !Number.isFinite(m)) return [];
  const last = new Date(y, m + 1, 0).getDate();
  const mm = String(m + 1).padStart(2, '0');
  return contiguousDateKeys(`${y}-${mm}-01`, `${y}-${mm}-${String(last).padStart(2, '0')}`);
}

/**
 * Date keys for the Master Tour day strip: a full calendar, not only show days.
 * Window is Jan 1 of the previous year through Dec 31 of the next year around the cursor,
 * expanded if the tour itself starts earlier or ends later.
 * @param {{ tourDateKeys?: string[], cursorDate?: Date, weekStartsOn?: number }} opts
 * @returns {string[]}
 */
export function dateKeysForDayStrip({ tourDateKeys, cursorDate } = {}) {
  const cursor = cursorDate instanceof Date && !Number.isNaN(cursorDate.getTime())
    ? cursorDate
    : new Date();
  let start = new Date(cursor.getFullYear() - 1, 0, 1);
  let end = new Date(cursor.getFullYear() + 1, 11, 31);
  const tour = [...new Set((tourDateKeys || []).map((k) => String(k || '').trim()).filter(Boolean))].sort();
  if (tour.length) {
    const first = parseDateKey(tour[0]);
    const last = parseDateKey(tour[tour.length - 1]);
    if (first && first.getTime() < start.getTime()) start = first;
    if (last && last.getTime() > end.getTime()) end = last;
  }
  return contiguousDateKeys(toDateKey(start), toDateKey(end));
}

export const DAY_STRIP_CELL = 192;
/** Shared row height for day-strip cells and chevron buttons. */
export const DAY_STRIP_ROW_HEIGHT = 88;
export const DAY_STRIP_WRAP_PAD_Y = 10;
export const DAY_STRIP_GAP = 6;
export const DAY_STRIP_MONTH_RULE = 26;
/** Days shown to the left of the selected/today cell (selected is 4th from the left). */
export const DAY_STRIP_PEEK_DAYS = 3;
/** 3–4 letter month marks stacked on the vertical month slice. */
export const MONTH_STACK_LABELS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUNE', 'JULY', 'AUG', 'SEPT', 'OCT', 'NOV', 'DEC'];

export function isMonthStartDateKey(dateKey) {
  return Number(String(dateKey || '').split('-')[2]) === 1;
}

export function dayStripMonthStackLabel(dateKey) {
  const d = parseDateKey(dateKey);
  if (!d) return '';
  return MONTH_STACK_LABELS[d.getMonth()] || '';
}

/** Vertical month rule sits before the 1st, except at the start of the strip. */
export function dayStripShowsMonthRule(keys, index) {
  if (index <= 0 || !Array.isArray(keys)) return false;
  return isMonthStartDateKey(keys[index]);
}

export function dayStripMonthRuleCount(keys, index) {
  if (!Array.isArray(keys) || index < 1) return 0;
  let n = 0;
  for (let j = 1; j <= index; j += 1) {
    if (isMonthStartDateKey(keys[j])) n += 1;
  }
  return n;
}

/** X offset of a day cell inside the strip, including month-rule gutters. */
export function dayStripItemX(keys, index, dims = {}) {
  const cellWidth = dims.cellWidth ?? DAY_STRIP_CELL;
  const gap = dims.gap ?? DAY_STRIP_GAP;
  const ruleWidth = dims.ruleWidth ?? DAY_STRIP_MONTH_RULE;
  if (!Array.isArray(keys) || index < 0) return 0;
  const rules = dayStripMonthRuleCount(keys, index);
  return index * cellWidth + rules * ruleWidth + (index + rules) * gap;
}

export function dayStripStartScrollX(keys, dateKey, dims) {
  const i = (keys || []).indexOf(dateKey);
  if (i < 0) return 0;
  return dayStripItemX(keys, i, dims);
}

/** Index of the cell that should sit at the left edge so `dateKey` is 4th from the left. */
export function dayStripPeekIndex(keys, dateKey, peekDays = DAY_STRIP_PEEK_DAYS) {
  const i = (keys || []).indexOf(dateKey);
  if (i < 0) return 0;
  const peek = Math.max(0, Number(peekDays) || 0);
  return Math.max(0, i - peek);
}

export function dayStripPeekScrollX(keys, dateKey, peekDays = DAY_STRIP_PEEK_DAYS, dims) {
  return dayStripItemX(keys, dayStripPeekIndex(keys, dateKey, peekDays), dims);
}

export function dayStripCenterScrollX(keys, dateKey, viewportWidth, dims) {
  const i = (keys || []).indexOf(dateKey);
  if (i < 0) return 0;
  const cellWidth = dims?.cellWidth ?? DAY_STRIP_CELL;
  const x = dayStripItemX(keys, i, dims);
  const vw = Math.max(0, Number(viewportWidth) || 0);
  return Math.max(0, x - vw / 2 + cellWidth / 2);
}

export function dayStripTodayOffscreen(keys, todayKey, scrollX, viewportWidth, dims) {
  const i = (keys || []).indexOf(todayKey);
  if (i < 0) return true;
  const cellWidth = dims?.cellWidth ?? DAY_STRIP_CELL;
  const x = dayStripItemX(keys, i, dims);
  const left = Number(scrollX) || 0;
  const vw = Math.max(0, Number(viewportWidth) || 0);
  return x + cellWidth <= left + 4 || x >= left + vw - 4;
}

export function dayStripIndexAtX(keys, scrollX, dims) {
  if (!Array.isArray(keys) || !keys.length) return 0;
  const x = Math.max(0, Number(scrollX) || 0);
  let best = 0;
  for (let i = 0; i < keys.length; i += 1) {
    if (dayStripItemX(keys, i, dims) <= x) best = i;
    else break;
  }
  return best;
}

export function monthLabel(year, month) {
  return new Date(year, month, 1).toLocaleString('en-US', { month: 'long', year: 'numeric' });
}

/** @param {unknown} s */
export function isValidDateKey(s) {
  if (s == null) return false;
  const t = String(s).trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(t);
}

/**
 * Coerce a tour day date (string, Date-ish, Firestore Timestamp-like) to YYYY-MM-DD or null.
 * Fixes sync when scheduledDate is ISO, unpadded "2025-3-5", or Timestamp.
 */
export function normalizeToDateKey(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'object') {
    if (typeof value.toDate === 'function') {
      try {
        const d = value.toDate();
        return Number.isNaN(d.getTime()) ? null : toDateKey(d);
      } catch {
        return null;
      }
    }
    if (typeof value.seconds === 'number') {
      const d = new Date(value.seconds * 1000);
      return Number.isNaN(d.getTime()) ? null : toDateKey(d);
    }
  }
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(s)) {
    const parts = s.split('-');
    const y = Number(parts[0]);
    const m = Number(parts[1]);
    const d = Number(parts[2]);
    const dt = new Date(y, m - 1, d);
    return Number.isNaN(dt.getTime()) ? null : toDateKey(dt);
  }
  const parsed = new Date(s);
  if (!Number.isNaN(parsed.getTime())) return toDateKey(parsed);
  return null;
}

/**
 * When importing a tour without per-day dates: start from **today** if the user is viewing
 * the current month; otherwise start from the **1st** of the visible month.
 */
export function getImportSequentialAnchor(visibleYear, visibleMonth) {
  const now = new Date();
  if (visibleYear === now.getFullYear() && visibleMonth === now.getMonth()) {
    return toDateKey(now);
  }
  return `${visibleYear}-${String(visibleMonth + 1).padStart(2, '0')}-01`;
}

/**
 * One YYYY-MM-DD per tour day. Uses each day’s `scheduledDate` when it normalizes to a date.
 * Otherwise the next date is the day after the previous tour day’s resolved date; the first gap uses
 * `tourEventStartDate` (if valid), else {@link getImportSequentialAnchor}.
 *
 * @param {Array<{ scheduledDate?: unknown }>} days
 * @param {number} visibleYear
 * @param {number} visibleMonth 0–11
 * @param {unknown} [tourEventStartDate] optional tour-level start (YYYY-MM-DD or similar)
 * @returns {string[]}
 */
export function computeImportDayDateKeys(days, visibleYear, visibleMonth, tourEventStartDate) {
  const anchorFromTour = tourEventStartDate != null && String(tourEventStartDate).trim() !== ''
    ? normalizeToDateKey(tourEventStartDate)
    : null;
  const anchor = anchorFromTour || getImportSequentialAnchor(visibleYear, visibleMonth);
  let lastResolved = null;
  return days.map((day) => {
    const normalized = normalizeToDateKey(day?.scheduledDate);
    if (normalized) {
      lastResolved = normalized;
      return normalized;
    }
    if (lastResolved === null) {
      lastResolved = anchor;
      return anchor;
    }
    const next = addDaysToKey(lastResolved, 1);
    lastResolved = next;
    return next;
  });
}

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** @param {string | null} dateKey */
function formatShortDate(dateKey) {
  const dt = parseDateKey(dateKey);
  if (!dt) return '';
  return `${MONTH_SHORT[dt.getMonth()]} ${dt.getDate()}, ${dt.getFullYear()}`;
}

/**
 * Inclusive date range for the active calendar view.
 * @param {'day' | 'week' | 'month' | 'year'} calView
 * @param {{ y: number, m: number }} cursor
 * @param {Date} cursorDate
 */
export function getCalendarViewDateRange(calView, cursor, cursorDate) {
  if (calView === 'year') {
    return {
      startKey: `${cursor.y}-01-01`,
      endKey: `${cursor.y}-12-31`,
      label: String(cursor.y),
    };
  }
  if (calView === 'month') {
    const lastDay = new Date(cursor.y, cursor.m + 1, 0).getDate();
    return {
      startKey: `${cursor.y}-${String(cursor.m + 1).padStart(2, '0')}-01`,
      endKey: `${cursor.y}-${String(cursor.m + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`,
      label: monthLabel(cursor.y, cursor.m),
    };
  }
  if (calView === 'week') {
    const start = new Date(cursorDate);
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() - start.getDay());
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    const sm = MONTH_SHORT[start.getMonth()];
    const em = MONTH_SHORT[end.getMonth()];
    const label = sm === em
      ? `${sm} ${start.getDate()}–${end.getDate()}, ${start.getFullYear()}`
      : `${sm} ${start.getDate()} – ${em} ${end.getDate()}, ${end.getFullYear()}`;
    return { startKey: toDateKey(start), endKey: toDateKey(end), label };
  }
  const key = toDateKey(cursorDate);
  const d = parseDateKey(key);
  const label = d ? formatShortDate(key) : key;
  return { startKey: key, endKey: key, label };
}

/**
 * @param {{ tour?: { days?: Array, eventStartDate?: unknown, eventEndDate?: unknown } }} folder
 * @returns {{ firstDate: string | null, lastDate: string | null, dayCount: number, scheduledDayCount: number, venueCount: number, hasDates: boolean, firstVenueLabel: string }}
 */
export function extractTourDateMeta(folder) {
  const days = folder?.tour?.days || [];
  const scheduledKeys = days
    .map((day) => normalizeToDateKey(day?.scheduledDate))
    .filter(Boolean)
    .sort();

  let firstDate = scheduledKeys[0] || null;
  let lastDate = scheduledKeys[scheduledKeys.length - 1] || null;

  if (!firstDate) firstDate = normalizeToDateKey(folder?.tour?.eventStartDate);
  if (!lastDate) lastDate = normalizeToDateKey(folder?.tour?.eventEndDate);

  const venueCount = days.reduce((n, day) => n + (Array.isArray(day?.venues) ? day.venues.length : 0), 0);
  let firstVenueLabel = '';
  for (const day of days) {
    for (const venue of day?.venues || []) {
      const name = String(venue?.name || '').trim();
      const city = String(venue?.city || '').trim();
      const state = String(venue?.state || '').trim();
      const location = [city, state].filter(Boolean).join(', ');
      firstVenueLabel = location ? `${name}${name ? ' · ' : ''}${location}` : name;
      if (firstVenueLabel) break;
    }
    if (firstVenueLabel) break;
  }

  return {
    firstDate,
    lastDate,
    dayCount: days.length,
    scheduledDayCount: scheduledKeys.length,
    venueCount,
    hasDates: Boolean(firstDate && lastDate),
    firstVenueLabel,
  };
}

/** @param {string | null} firstDate @param {string | null} lastDate */
export function formatTourDateRange(firstDate, lastDate) {
  if (!firstDate && !lastDate) return 'No dates scheduled';
  if (!firstDate || !lastDate) return formatShortDate(firstDate || lastDate);
  if (firstDate === lastDate) return formatShortDate(firstDate);

  const start = parseDateKey(firstDate);
  const end = parseDateKey(lastDate);
  if (!start || !end) return `${firstDate} – ${lastDate}`;

  if (start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth()) {
    return `${MONTH_SHORT[start.getMonth()]} ${start.getDate()}–${end.getDate()}, ${start.getFullYear()}`;
  }
  if (start.getFullYear() === end.getFullYear()) {
    return `${MONTH_SHORT[start.getMonth()]} ${start.getDate()} – ${MONTH_SHORT[end.getMonth()]} ${end.getDate()}, ${start.getFullYear()}`;
  }
  return `${formatShortDate(firstDate)} – ${formatShortDate(lastDate)}`;
}

function tourOverlapsRange(firstDate, lastDate, viewStart, viewEnd) {
  if (!firstDate || !lastDate || !viewStart || !viewEnd) return false;
  return firstDate <= viewEnd && lastDate >= viewStart;
}

/** @returns {'unscheduled' | 'past' | 'upcoming' | 'active'} */
function getTourTimelineStatus(firstDate, lastDate, todayKey) {
  if (!firstDate || !lastDate) return 'unscheduled';
  if (lastDate < todayKey) return 'past';
  if (firstDate > todayKey) return 'upcoming';
  return 'active';
}

/**
 * Sort tours for the calendar picker: current-view overlap first, then upcoming, then past.
 * @param {Array<{ id: string, name?: string, tour?: object }>} tours
 * @param {{ startKey: string, endKey: string }} viewRange
 * @param {string} todayKey YYYY-MM-DD
 */
export function sortToursForCalendarView(tours, viewRange, todayKey) {
  const { startKey, endKey } = viewRange;
  const enriched = (tours || []).map((tour) => {
    const meta = extractTourDateMeta(tour);
    const inView = tourOverlapsRange(meta.firstDate, meta.lastDate, startKey, endKey);
    const status = getTourTimelineStatus(meta.firstDate, meta.lastDate, todayKey);
    return { ...tour, meta, inView, status };
  });

  enriched.sort((a, b) => {
    if (a.inView !== b.inView) return a.inView ? -1 : 1;

    const statusOrder = { active: 0, upcoming: 1, past: 2, unscheduled: 3 };
    if (statusOrder[a.status] !== statusOrder[b.status]) {
      return statusOrder[a.status] - statusOrder[b.status];
    }

    if (a.status === 'active' || a.status === 'upcoming') {
      return (a.meta.firstDate || '9999-99-99').localeCompare(b.meta.firstDate || '9999-99-99');
    }
    if (a.status === 'past') {
      return (b.meta.lastDate || '').localeCompare(a.meta.lastDate || '');
    }
    return String(a.name || '').localeCompare(String(b.name || ''));
  });

  return enriched;
}

/** @typedef {'all' | 'inView' | 'upcoming' | 'past' | 'unscheduled' | 'hidden' | 'visible'} TourPickerFilter */

/**
 * @param {ReturnType<typeof sortToursForCalendarView>} tours
 * @param {TourPickerFilter} filter
 * @param {Set<string>} hiddenTourIds
 */
export function filterToursForPicker(tours, filter, hiddenTourIds) {
  const hidden = hiddenTourIds || new Set();
  switch (filter) {
    case 'inView':
      return tours.filter((t) => t.inView);
    case 'upcoming':
      return tours.filter((t) => t.status === 'upcoming' || t.status === 'active');
    case 'past':
      return tours.filter((t) => t.status === 'past');
    case 'unscheduled':
      return tours.filter((t) => t.status === 'unscheduled');
    case 'hidden':
      return tours.filter((t) => hidden.has(t.id));
    case 'visible':
      return tours.filter((t) => !hidden.has(t.id));
    case 'all':
    default:
      return tours;
  }
}

/**
 * @param {ReturnType<typeof sortToursForCalendarView>} tours
 * @param {string} viewLabel
 */
export function groupToursForPickerDisplay(tours, viewLabel) {
  /** @type {Array<{ key: string, title: string, items: typeof tours }>} */
  const groups = [];
  const inView = tours.filter((t) => t.inView);
  const upcoming = tours.filter((t) => !t.inView && (t.status === 'upcoming' || t.status === 'active'));
  const past = tours.filter((t) => !t.inView && t.status === 'past');
  const unscheduled = tours.filter((t) => t.status === 'unscheduled');

  if (inView.length) groups.push({ key: 'inView', title: `In ${viewLabel}`, items: inView });
  if (upcoming.length) groups.push({ key: 'upcoming', title: 'Upcoming', items: upcoming });
  if (past.length) groups.push({ key: 'past', title: 'Past', items: past });
  if (unscheduled.length) groups.push({ key: 'unscheduled', title: 'No dates scheduled', items: unscheduled });
  return groups;
}

/** @param {ReturnType<typeof sortToursForCalendarView>} tours @param {TourPickerFilter} filter */
export function countToursForPickerFilter(tours, filter, hiddenTourIds) {
  return filterToursForPicker(tours, filter, hiddenTourIds).length;
}

/** @typedef {'this_day' | 'this_and_after' | 'this_and_before' | 'all_tour_days'} DayApplyScope */

export const DAY_APPLY_SCOPES = {
  THIS_DAY: 'this_day',
  THIS_AND_AFTER: 'this_and_after',
  THIS_AND_BEFORE: 'this_and_before',
  ALL_TOUR_DAYS: 'all_tour_days',
};

export const DAY_APPLY_SCOPE_OPTIONS = [
  { id: DAY_APPLY_SCOPES.THIS_DAY, label: 'This day only' },
  { id: DAY_APPLY_SCOPES.THIS_AND_AFTER, label: 'This day & after' },
  { id: DAY_APPLY_SCOPES.THIS_AND_BEFORE, label: 'This day & before' },
  { id: DAY_APPLY_SCOPES.ALL_TOUR_DAYS, label: 'All tour days' },
];

/**
 * Sorted unique YYYY-MM-DD show dates from tour planner day rows.
 * @param {Array<{ scheduledDate?: unknown }> | null | undefined} tourDays
 * @returns {string[]}
 */
export function getTourShowDateKeys(tourDays) {
  const keys = (tourDays || [])
    .map((day) => normalizeToDateKey(day?.scheduledDate))
    .filter(Boolean);
  return [...new Set(keys)].sort();
}

/**
 * Fallback show dates from imported calendar events when planner rows lack dates.
 * @param {Array<{ date?: unknown, sourceTourId?: string }>} tourEvents
 * @param {string} tourId
 * @returns {string[]}
 */
export function getTourShowDateKeysFromEvents(tourEvents, tourId) {
  if (!tourId) return [];
  const keys = (tourEvents || [])
    .filter((ev) => ev?.sourceTourId === tourId)
    .map((ev) => normalizeToDateKey(ev?.date))
    .filter(Boolean);
  return [...new Set(keys)].sort();
}

/**
 * Resolve which tour show dates should receive an edit from the anchor day.
 * @param {string} anchorDateKey
 * @param {DayApplyScope} scope
 * @param {string[]} tourShowDateKeys sorted unique show dates
 * @returns {string[]}
 */
export function resolveDayApplyScopeDateKeys(anchorDateKey, scope, tourShowDateKeys) {
  const anchor = normalizeToDateKey(anchorDateKey);
  const sorted = [...new Set((tourShowDateKeys || []).filter(Boolean))].sort();
  if (!anchor) return [];
  if (scope === DAY_APPLY_SCOPES.ALL_TOUR_DAYS) {
    return sorted.length ? sorted : [anchor];
  }
  if (scope === DAY_APPLY_SCOPES.THIS_AND_AFTER) {
    const scoped = sorted.filter((d) => d >= anchor);
    return scoped.length ? scoped : [anchor];
  }
  if (scope === DAY_APPLY_SCOPES.THIS_AND_BEFORE) {
    const scoped = sorted.filter((d) => d <= anchor);
    return scoped.length ? scoped : [anchor];
  }
  return [anchor];
}

/**
 * @param {DayApplyScope} scope
 * @param {string} anchorDateKey
 * @param {string[]} tourShowDateKeys
 */
export function countDayApplyScopeDates(scope, anchorDateKey, tourShowDateKeys) {
  return resolveDayApplyScopeDateKeys(anchorDateKey, scope, tourShowDateKeys).length;
}

/**
 * Which tour should be managed for a date. One tour → select it.
 * Several tours → keep the current one if it is on the day, otherwise none.
 */
export function pickTourIdForDate(toursOnDay, currentTourId = null) {
  const tours = Array.isArray(toursOnDay) ? toursOnDay : [];
  const ids = tours.map((t) => t?.tourId || t?.id).filter(Boolean);
  if (ids.length === 1) return ids[0];
  if (ids.length > 1) {
    if (currentTourId && ids.includes(currentTourId)) return currentTourId;
    return null;
  }
  return currentTourId || null;
}

/**
 * Format a clock string for the design-bar 12/24 toggle.
 * Accepts "16:00", "4:00 PM", or "4:00".
 */
export function formatClockTime(raw, timeFormat = '12-hour') {
  const t = String(raw || '').trim();
  if (!t || t === '—') return t || '—';
  const m = t.match(/^(\d{1,2}):(\d{2})(?:\s*([AaPp][Mm]))?$/);
  if (!m) return t;
  let h = Number(m[1]);
  const min = m[2];
  const period = m[3] ? m[3].toUpperCase() : null;
  if (period) {
    if (period === 'AM') h = h % 12;
    else h = (h % 12) + 12;
  }
  if (!Number.isFinite(h) || h < 0 || h > 23) return t;
  if (timeFormat === '12-hour') {
    const am = h < 12;
    return `${h % 12 || 12}:${min} ${am ? 'AM' : 'PM'}`;
  }
  return `${String(h).padStart(2, '0')}:${min}`;
}

/** Rewrite HH:MM (optional AM/PM) tokens in a schedule preview blob. */
export function applyTimeFormatToText(text, timeFormat = '12-hour') {
  return String(text || '').replace(/\b(\d{1,2}:\d{2})(?:\s*([AaPp][Mm]))?\b/g, (full) => (
    formatClockTime(full, timeFormat)
  ));
}

/**
 * Master Tour Calendar chrome: tour title + MAP/MONTH/WEEK pill share one row
 * on desktop. Below this width they stack so the pill cannot paint over the title.
 */
export const MASTER_TOUR_HEADER_STACK_BELOW = 1280;

export function shouldStackMasterTourHeader(windowWidth) {
  const width = Number(windowWidth);
  return Number.isFinite(width) && width > 0 && width < MASTER_TOUR_HEADER_STACK_BELOW;
}

/** Earliest dated tour day, or earliest calendar event for that tour. */
export function deriveTourStartDateKey(folder, events = []) {
  const dayDates = (folder?.tour?.days || [])
    .map((d) => String(d?.scheduledDate || '').trim())
    .filter((d) => parseDateKey(d));
  if (dayDates.length) return dayDates.sort()[0];
  const eventDates = (events || [])
    .filter((e) => e && e.sourceTourId === folder?.id)
    .map((e) => String(e.date || '').trim())
    .filter((d) => parseDateKey(d));
  if (eventDates.length) return eventDates.sort()[0];
  return '';
}

/** Cursor + selected day to land on the first tour date in month and week views. */
export function tourStartFocus(folder, events = []) {
  const dateKey = deriveTourStartDateKey(folder, events);
  const date = parseDateKey(dateKey);
  if (!date) return null;
  return { dateKey, date, year: date.getFullYear(), month: date.getMonth() };
}

/**
 * Column width so a short tour fills the timeline the same way a long one does.
 * Long tours stay at minCol and scroll; short tours stretch up to maxCol.
 */
export function timelineColumnWidth(dayCount, availableWidth, labelWidth = 132, minCol = 42, maxCol = 128) {
  const n = Math.max(1, Number(dayCount) || 1);
  const usable = Math.max(0, Number(availableWidth) || 0) - Number(labelWidth || 0) - 38;
  if (usable <= 0) return minCol;
  return Math.min(maxCol, Math.max(minCol, Math.floor(usable / n)));
}
